import { Injectable, effect, inject, signal } from '@angular/core';
import { AuthService } from '../../../core/auth/services/auth.service';
import { OpportunityService } from '../../../core/services/opportunity.service';

/**
 * Favoris d'opportunités (UC-10), portés par opportunity-service : POST/DELETE .../{id}/bookmark
 * et GET .../bookmarks/me. `savedIds` est indexé par l'UUID réel de l'opportunité (pas le slug).
 * Mises à jour optimistes avec retour en arrière si l'appel échoue.
 */
@Injectable({ providedIn: 'root' })
export class SavedOpportunitiesStore {
  private readonly opportunityService = inject(OpportunityService);
  private readonly auth = inject(AuthService);

  readonly savedIds = signal<ReadonlySet<string>>(new Set());

  constructor() {
    effect(() => {
      if (this.auth.isAuthenticated()) {
        this.refresh();
      } else {
        this.savedIds.set(new Set());
      }
    });
  }

  private refresh(): void {
    this.opportunityService.myBookmarks().subscribe({
      next: (list) => this.savedIds.set(new Set(list.map((o) => o.id))),
      error: () => undefined,
    });
  }

  isSaved(opportunityId: string): boolean {
    return this.savedIds().has(opportunityId);
  }

  toggle(opportunityId: string): void {
    if (this.isSaved(opportunityId)) {
      this.remove(opportunityId);
    } else {
      this.add(opportunityId);
    }
  }

  private add(opportunityId: string): void {
    this.savedIds.update((ids) => new Set(ids).add(opportunityId));
    this.opportunityService.bookmark(opportunityId).subscribe({
      error: () => this.dropLocally(opportunityId),
    });
  }

  remove(opportunityId: string): void {
    const wasSaved = this.isSaved(opportunityId);
    this.dropLocally(opportunityId);
    this.opportunityService.removeBookmark(opportunityId).subscribe({
      error: () => {
        if (wasSaved) this.savedIds.update((ids) => new Set(ids).add(opportunityId));
      },
    });
  }

  private dropLocally(opportunityId: string): void {
    this.savedIds.update((ids) => {
      if (!ids.has(opportunityId)) return ids;
      const next = new Set(ids);
      next.delete(opportunityId);
      return next;
    });
  }
}
